import { Navigate, Route, Routes, useLocation } from "react-router";
import { AppShell } from "./shell/AppShell";
import { ProtectedRoute } from "./shell/ProtectedRoute";
import { OAuthConsentPage } from "./loxley/OAuthConsentPage";
import { TenantAppRedirect } from "./shell/TenantAppRedirect";
import { useAuth } from "./shell/AuthProvider";
import { appUrl } from "./lib/customerUrl";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { AccountPage } from "./pages/AccountPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PlaceholderAppPage } from "./pages/PlaceholderAppPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SetupPasswordPage } from "./pages/SetupPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { HelpPage } from "./pages/HelpPage";
import { QuoteAcceptPage, UnsubscribePage } from "./pages/PublicActionPages";
import { SignupRedirectPage } from "./pages/SignupPage";
import { SsoMockPage } from "./pages/SsoMockPage";
import { SsoCallbackPage } from "./pages/SsoCallbackPage";
import { TimesheetApp } from "./pages/workforce/TimesheetApp";
import { ExpenseApp } from "./pages/workforce/ExpenseApp";
import { CrmApp } from "./pages/crm/CrmApp";
import { ErrorPage } from "./pages/ErrorPage";
import { TenantRedirect } from "./shell/TenantRedirect";

function AppEntryRedirect({ app }: { app: "timesheet" | "expense" }) {
  const { session } = useAuth();
  const location = useLocation();
  const prefix = `/${app}`;
  const tail = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : "";
  const path = tail.startsWith("/") ? tail : "/";
  return (
    <Navigate
      to={appUrl(app, session?.orgSlug, `${path}${location.search}${location.hash}`)}
      replace
    />
  );
}

/**
 * Route table. AppShell/ProtectedRoute here take `children` directly
 * (not <Outlet/>) — this repo's own composition style, distinct from
 * IntelliloxGRC's outlet-based shell. Every business app resolves to
 * PlaceholderAppPage via the single "/apps/:appCode" route until it's
 * actually built.
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupRedirectPage />} />
      <Route path="/sso/mock" element={<SsoMockPage />} />
      <Route path="/sso/callback" element={<SsoCallbackPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/quote-accept/:token" element={<QuoteAcceptPage />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/error/:code" element={<ErrorPage />} />
      <Route path="/oauth/consent" element={<ProtectedRoute><OAuthConsentPage /></ProtectedRoute>} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/home" element={<Navigate to="/dashboard" replace />} />
                {/*
                  Legacy un-slugged links/bookmarks, and every internal fallback that
                  navigates here before an orgSlug is known (SSO callback, TenantAppRedirect's
                  own no-slug case, ExpenseApp/TimesheetApp's no-org guard) — all redirected
                  to the customer-unique URL below once the session resolves.
                */}
                <Route path="/dashboard" element={<TenantRedirect to="/dashboard" />} />
                <Route path="/account" element={<TenantRedirect to="/account" />} />
                <Route path="/profile" element={<TenantRedirect to="/account" />} />
                <Route path="/contacts" element={<TenantRedirect to="/contacts" />} />
                <Route path="/targets" element={<TenantRedirect to="/targets" />} />
                <Route path="/tasks" element={<TenantRedirect to="/tasks" />} />
                <Route path="/activities" element={<TenantRedirect to="/activities" />} />
                <Route path="/reports" element={<TenantRedirect to="/reports" />} />
                <Route path="/search" element={<TenantRedirect to="/search" />} />
                <Route path="/notifications" element={<TenantRedirect to="/notifications" />} />
                <Route path="/settings" element={<TenantRedirect to="/settings" />} />
                <Route path="/billing" element={<TenantRedirect to="/settings" tab="billing" />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/timesheet" element={<AppEntryRedirect app="timesheet" />} />
                <Route path="/timesheet/*" element={<AppEntryRedirect app="timesheet" />} />
                <Route path="/expense" element={<AppEntryRedirect app="expense" />} />
                <Route path="/expense/*" element={<AppEntryRedirect app="expense" />} />
                <Route
                  path="/apps/crm/:orgSlug/dashboard"
                  element={<TenantAppRedirect><HomePage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/dashboard"
                  element={<TenantAppRedirect><HomePage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/:orgSlug/account"
                  element={<TenantAppRedirect><AccountPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/:orgSlug/profile"
                  element={<TenantAppRedirect><AccountPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/profile"
                  element={<TenantAppRedirect><AccountPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/account"
                  element={<TenantAppRedirect><AccountPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/:orgSlug/settings"
                  element={<TenantAppRedirect><SettingsPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/settings"
                  element={<TenantAppRedirect><SettingsPage /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/:orgSlug/*"
                  element={<TenantAppRedirect><CrmApp /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/crm/*"
                  element={<TenantAppRedirect><CrmApp /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/timesheet/:orgSlug/*"
                  element={<TenantAppRedirect><TimesheetApp /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/timesheet/*"
                  element={<TenantAppRedirect><TimesheetApp /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/expense/:orgSlug/*"
                  element={<TenantAppRedirect><ExpenseApp /></TenantAppRedirect>}
                />
                <Route
                  path="/apps/expense/*"
                  element={<TenantAppRedirect><ExpenseApp /></TenantAppRedirect>}
                />
                <Route path="/apps/:appCode" element={<PlaceholderAppPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
